import React, { useState, useEffect } from 'react';
import {
  createAsideEntry,
  updateAsideEntry,
  deleteAsideEntry,
} from '../../api/api';
import './AsideModal.css';

const AsideModal = ({
  entry,
  onClose,
  onSave,
  entries = [],
  mode = 'add',
  typeForSelection = null,
  onSelectEdit = () => {},
}) => {
  const isEdit = !!entry?.id && mode === 'edit';
  const isSelectionMode = mode === 'select-edit' || mode === 'select-delete';

  // eslint-disable-next-line
  const isAddMode = mode === 'add';

  const initialForm = {
    type: entry?.type || typeForSelection || 'info',
    title: entry?.title || '',
    subtitle: entry?.subtitle || '',
    description: entry?.description || '',
    url: entry?.url || '',
    icon: null,
  };

  const [formData, setFormData] = useState(initialForm);
  const [previewIcon, setPreviewIcon] = useState(entry?.iconUrl || '');
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (entry?.iconUrl) setPreviewIcon(entry.iconUrl);

    if (entry) {
      setFormData({
        type: entry.type || typeForSelection || 'info',
        title: entry.title || '',
        subtitle: entry.subtitle || '',
        description: entry.description || '',
        url: entry.url || '',
        icon: null,
      });
    }
  }, [entry, typeForSelection]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'icon' && files?.[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, icon: file }));
      setPreviewIcon(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append('type', formData.type);
    data.append('title', formData.title || '');
    data.append('subtitle', formData.subtitle || '');
    data.append('description', formData.description || '');
    data.append('url', formData.url || '');

    if (formData.icon) {
      data.append('icon', formData.icon);
    }

    try {
      let result;
      if (isEdit) {
        //console.log('UPDATE ID:', entry.id, typeof entry.id);
        //console.log('CATEGORY:', formData.category);
        result = await updateAsideEntry(Number(entry.id), data);
      } else {
        result = await createAsideEntry(data);
      }
      onSave(result);
      onClose();
    } catch (err) {
      console.error('Błąd zapisu aside:', err);
      alert('Błąd zapisu. Spróbuj ponownie.');
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedId) return alert('Wybierz wpis do usunięcia');

    const id = selectedId;
    const selected = entries.find((e) => String(e.id) === String(id));

    if (!selected) return alert(`Nie znaleziono wpisu o ID: ${id}`);

    if (
      !window.confirm(
        `Na pewno chcesz usunąć "${selected.title || selected.subtitle}"?`
      )
    )
      return;

    try {
      await deleteAsideEntry(id);
      onSave(null); // odśwież
      alert('Usunięto pomyślnie');
      onClose();
    } catch (err) {
      alert('Błąd usuwania: ' + err.message);
    }
  };

  const handleEditSelected = () => {
    if (!selectedId) return alert('Wybierz wpis do edycji');

    const id = selectedId;
    const selected = entries.find((e) => String(e.id) === String(id));

    if (!selected) return alert(`Nie znaleziono wpisu o ID: ${id}`);

    onSelectEdit(selected);
  };

  // Tryb wyboru wpisu (lista do edycji/usunięcia)
  if (isSelectionMode) {
    //console.log('Tryb wyboru:', mode, 'Typ:', typeForSelection);
    //console.log('Przekazane entries:', entries);

    const filtered = entries;

    //console.log('Po filtracji:', filtered);

    return (
      <div className="aside-modal-overlay" onClick={onClose}>
        <div
          className="aside-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>

          <h2>
            {mode === 'select-edit'
              ? 'Wybierz wpis do edycji'
              : 'Wybierz wpis do usunięcia'}
          </h2>

          {filtered.length === 0 ? (
            <p>
              Brak wpisów tego typu (sprawdź konsolę – typ: {typeForSelection})
            </p>
          ) : (
            <div className="form-group">
              <label>Wybierz wpis</label>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value="">— wybierz —</option>
                {filtered.map((item) => (
                  <option key={item.id} value={item.id.toString()}>
                    {item.type === 'personal'
                      ? item.title || item.subtitle
                      : item.type === 'info'
                        ? item.title
                        : item.title || item.url}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="modal-actions">
            {mode === 'select-edit' ? (
              <button
                type="button"
                className="save-btn"
                disabled={!selectedId}
                onClick={handleEditSelected}
              >
                Edytuj wybrany
              </button>
            ) : (
              <button
                type="button"
                className="delete-btn"
                disabled={!selectedId}
                onClick={handleDeleteSelected}
              >
                Usuń wybrany
              </button>
            )}
            <button type="button" className="cancel-btn" onClick={onClose}>
              Anuluj
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tryb dodawania / edycji
  return (
    <div className="aside-modal-overlay" onClick={onClose}>
      <div className="aside-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          ×
        </button>

        <h2>{isEdit ? 'Edytuj wpis' : 'Dodaj nowy wpis'}</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Typ wpisu *</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              disabled={isEdit}
            >
              <option value="">Wybierz...</option>
              <option value="personal">
                Dane osobowe (imię, nazwisko, status)
              </option>
              <option value="info">
                Informacja (email, telefon, lokalizacja, uczelnia)
              </option>
              <option value="social">Social media (link + ikona)</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              {formData.type === 'personal'
                ? 'Imię i nazwisko *'
                : formData.type === 'info'
                  ? 'Tytuł (np. Email, Telefon) *'
                  : 'Nazwa serwisu (np. Instagram) *'}
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder={
                formData.type === 'personal'
                  ? 'Mikołaj Melnyk'
                  : formData.type === 'info'
                    ? 'Email'
                    : 'Instagram'
              }
            />
          </div>

          <div className="form-group">
            <label>
              {formData.type === 'personal'
                ? 'Status (zawód) *'
                : formData.type === 'info'
                  ? 'Wartość (np. adres email, numer telefonu) *'
                  : 'Link *'}
            </label>
            <input
              type={formData.type === 'social' ? 'url' : 'text'}
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              required
              placeholder={
                formData.type === 'personal'
                  ? 'Web Developer'
                  : formData.type === 'info'
                    ? 'mikolajmelnyk16@gmail.com'
                    : 'https://instagram.com/twojnick'
              }
            />
          </div>

          {(formData.type === 'social' ||
            formData.type === 'personal' ||
            formData.type === 'info') && (
            <div className="form-group">
              <label>
                {formData.type === 'social'
                  ? 'Ikona / Logo (obowiązkowa dla social)'
                  : formData.type === 'personal'
                    ? 'Zdjęcie / Avatar (opcjonalne dla personal)'
                    : 'Ikona / Logo (opcjonalne dla info)'}
              </label>
              <input
                type="file"
                name="icon"
                accept="image/*"
                onChange={handleChange}
                required={formData.type === 'social' && !isEdit} // tylko social wymaga
              />
              {previewIcon && (
                <div className="preview-icon">
                  <img
                    src={previewIcon}
                    alt="Podgląd"
                    style={{
                      width: formData.type === 'personal' ? '100px' : '50px',
                      borderRadius: formData.type === 'personal' ? '50%' : '0',
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button type="submit" className="save-btn">
              {isEdit ? 'Zapisz zmiany' : 'Dodaj wpis'}
            </button>

            {isEdit && (
              <button
                type="button"
                className="delete-btn"
                onClick={async () => {
                  if (
                    !window.confirm(`Na pewno chcesz usunąć "${entry.title}"?`)
                  )
                    return;
                  try {
                    await deleteAsideEntry(Number(entry.id));
                    onSave(null);
                    alert('Usunięto pomyślnie');
                    onClose();
                  } catch (err) {
                    alert('Błąd usuwania: ' + err.message);
                  }
                }}
              >
                Usuń wpis
              </button>
            )}

            <button type="button" className="cancel-btn" onClick={onClose}>
              Anuluj
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AsideModal;
